import { useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
// One icon per destination, none shared: the rail is scanned by shape. Lucide,
// the family ds/ uses.
import {
  BookOpen,
  Building2,
  ClipboardCheck,
  CloudUpload,
  Database,
  Download,
  FileBadge,
  FileClock,
  FlaskConical,
  GitBranch,
  History,
  IdCard,
  Laptop,
  LayoutDashboard,
  MonitorSmartphone,
  PackageCheck,
  Scale,
  ScanText,
  ScrollText,
  Settings,
  ShieldAlert,
  ShieldBan,
  UserRound,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { AppHeader, AppSidebar, Timestamp, type AppSidebarItem, type AppSidebarSection } from '@/components/ds-dlp';
import { useThemeMode } from '@/lib/theme';
import { BRAND } from '@/brand';
import { useRailTooltip, useSmoothHoverPill } from '@/components/railTooltip';
import { useAuth } from '@/contexts/AuthContext';
import { Perm } from '@/auth/permissions';
import { useTenant } from '@/features/tenants/hooks';
import { ChangePasswordDialog } from '@/features/account/ChangePasswordDialog';
import { clearPasswordPromptSnooze, passwordPromptDue, snoozePasswordPrompt } from '@/features/account/passwordPrompt';
import { fallBackToBrandMark, useTenantLogo } from '@/lib/useTenantLogo';
import { navGroupOrder, routes, type RouteSpec } from './routes';
import { useHeaderNotifications } from './HeaderNotifications';
import { HeaderStatus } from './HeaderStatus';

// Path → sidebar icon. Kept here rather than in routes.tsx so the route
// registry stays free of UI imports.
const ICON_BY_PATH: Record<string, LucideIcon> = {
  '/dashboard': LayoutDashboard,
  '/blocked-events': ShieldBan,
  '/decisions': Scale,
  '/policies': ScrollText,
  '/policies/tls-rejections': FileBadge,
  '/simulator': FlaskConical,
  '/dlp/rules': ScanText,
  '/upload-blocking': CloudUpload,
  '/tampering': ShieldAlert,
  '/compliance': ClipboardCheck,
  '/agents': Laptop,
  '/agent-logs': FileClock,
  '/device-binding': MonitorSmartphone,
  '/downloads': Download,
  '/groups': Users,
  '/users': UserRound,
  '/audit': History,
  '/admin/agents/versions': PackageCheck,
  '/admin/roles': IdCard,
  '/platform/tenants': Building2,
  '/platform/schema-health': Database,
  '/platform/migrations': GitBranch,
  '/settings': Settings,
  '/user-manual': BookOpen,
};

const SIDEBAR_COLLAPSED_KEY = 'dlp_sidebar_collapsed';

/**
 * The header clock (cascade plan 3.2): the current time as a ds Timestamp,
 * which follows the reader's local/UTC preference and shows the other zone on
 * hover. It ticks each minute, not each second: the old per-second clock
 * re-rendered the whole shell every second for a digit nobody reads.
 */
function HeaderClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tick = () => setNow(new Date());
    // Align to the next minute boundary, then every minute.
    const first = setTimeout(() => {
      tick();
      interval = setInterval(tick, 60_000);
    }, 60_000 - (Date.now() % 60_000));
    let interval: ReturnType<typeof setInterval> | undefined;
    return () => {
      clearTimeout(first);
      if (interval) clearInterval(interval);
    };
  }, []);
  return <Timestamp value={now} noSeconds className="rc-appheader__clock hidden lg:inline" />;
}

/** The routes the user may open that appear in the nav, grouped and ordered as routes.tsx says. */
function navSections(hasPermission: (code: string) => boolean): { group: string; items: RouteSpec[] }[] {
  const visible = routes.filter((r) => r.navLabel && (r.perm == null || hasPermission(r.perm)));
  const groups = new Map<string, RouteSpec[]>();
  for (const r of visible) {
    const g = r.navGroup ?? 'Other';
    groups.set(g, [...(groups.get(g) ?? []), r]);
  }
  const order = [
    ...navGroupOrder.filter((g) => groups.has(g)),
    ...[...groups.keys()].filter((g) => !navGroupOrder.includes(g)).sort(),
  ];
  return order.map((group) => ({ group, items: groups.get(group)! }));
}

interface LayoutProps {
  children: ReactNode;
}

/**
 * The app shell on the d2d design-system components (cascade plan 3.2), laid
 * out as d2d-XDR's AppFrame and ported from d2d-ZTA's Layout: the rail in flow
 * beside a column that holds the header and the one scrolling region. Below lg
 * the rail slides in over the page from the header's menu button. The account
 * menu holds the user's name and email, the theme switcher and sign-out. The
 * header's first item is the tenant chip (eval F17), on every route.
 *
 * The old topbar's threat-level pill (hard-coded to "low") and its "LIVE"
 * pulse (on whether or not anything was live) showed data that was not real.
 * Both are back, driven by real data: HeaderStatus takes the threat level from
 * the open alerts of the last 24 hours, and Live from whether its poll lands.
 */
export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const notifications = useHeaderNotifications();
  const { user, logout, hasPermission, tenantSlug } = useAuth();
  // Eval F17: the tenant chip. Platform scope is tenant.manage, the one
  // permission no tenant role holds (go-api rbac_seed: it is "platform-level"
  // and kept off the tenant Admin role); it is what gates /platform/*. Such a
  // user still signs in to a tenant, whose data the other pages show, so the
  // chip names it after "Platform". /auth/me carries the slug only; the name
  // comes from /platform/tenants/:slug, which only platform staff may read,
  // so everyone else sees the slug.
  const platformScope = hasPermission(Perm.TenantManage);
  const { data: tenantRecord } = useTenant(platformScope && tenantSlug ? tenantSlug : undefined);
  // Resolved light/dark, live: follows an OS switch under "system".
  const { resolvedMode } = useThemeMode();
  // The tenant's white-label logo (Settings > Branding), else the brand mark.
  const logo = useTenantLogo(tenantSlug);

  // Account menu > Change password. It opens by itself when an administrator
  // set the password, unless "Later" put it off for the day
  // (passwordPrompt.ts). A change signs out every session.
  const mustChangePassword = !!user?.must_change_password;
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [passwordPrompted, setPasswordPrompted] = useState(false);
  useEffect(() => {
    if (mustChangePassword && !passwordPrompted && user && passwordPromptDue(tenantSlug, user.id)) {
      setPasswordPrompted(true);
      setPasswordOpen(true);
    }
  }, [mustChangePassword, passwordPrompted, user, tenantSlug]);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Persisted across reloads, as d2d-XDR's rail is. Storage can be blocked
  // (private mode); the rail then simply starts expanded.
  const [collapsed, setCollapsedState] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
    } catch {
      return false;
    }
  });
  const setCollapsed = (next: boolean) => {
    setCollapsedState(next);
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
    } catch {
      /* not remembered this time */
    }
  };
  // Collapsed rail: each icon's name pops out beside the rail, and the hover
  // pill glides between rows (d2d-XDR, via d2d-ZTA).
  const railTip = useRailTooltip(collapsed, true);
  useSmoothHoverPill(railTip.ref, true);

  // Close the mobile drawer once a nav link has navigated.
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Exactly one nav link is current: the longest path that matches, so
  // /agents/:id lights Agents and /admin/agents/publish lights Agent Versions.
  const matches = (path: string) => location.pathname === path || location.pathname.startsWith(path + '/');
  const current =
    routes.filter((r) => r.navLabel && matches(r.path)).sort((a, b) => b.path.length - a.path.length)[0] ??
    (location.pathname.startsWith('/admin/agents/') ? routes.find((r) => r.path === '/admin/agents/versions') : undefined);

  // Browser tab title: "<page> · d2d-DLP".
  useEffect(() => {
    document.title = current?.navLabel ? `${current.navLabel} · ${BRAND.name}` : BRAND.name;
  }, [current?.navLabel]);

  const renderLink = (props: { href: string; 'aria-current'?: 'page'; className?: string; children: ReactNode }): ReactElement => {
    const { href, children: linkChildren, ...rest } = props;
    return (
      <Link to={href} {...rest}>
        {linkChildren}
      </Link>
    );
  };

  const sections: AppSidebarSection[] = navSections(hasPermission).map(({ group, items }) => ({
    id: group.toLowerCase(),
    title: group,
    items: items.map((r): AppSidebarItem => {
      const Icon = ICON_BY_PATH[r.path] ?? LayoutDashboard;
      return { id: r.path, label: r.navLabel, icon: <Icon size={20} />, href: r.path, active: current?.path === r.path };
    }),
  }));

  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ');
  const userName = fullName || user?.email || 'User';

  return (
    // overflow-clip, not overflow-hidden: a hidden box is still scrollable by
    // script, so a scrollIntoView could scroll the whole shell up and leave a
    // blank band under it. A clipped box cannot scroll.
    <div className="flex h-[100dvh] overflow-clip bg-[var(--d2d-bg-primary)]">
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-[var(--dlp-scrim)] lg:hidden" onClick={() => setSidebarOpen(false)} aria-hidden="true" />
      )}

      <aside
        className={
          'fixed inset-y-0 left-0 z-50 transition-transform duration-300 lg:static lg:z-auto lg:translate-x-0 motion-reduce:transition-none ' +
          (sidebarOpen ? 'translate-x-0' : '-translate-x-full')
        }
      >
        <AppSidebar
          ref={railTip.ref}
          aria-label="Main navigation"
          theme={resolvedMode}
          brand={{
            logo: logo ? (
              // Capped to the 40px mark box: a wide logo shrinks to fit
              // instead of spilling under the wordmark.
              <img src={logo} alt="" aria-hidden="true" className="rc-sidenav__brand-mark max-w-[36px] object-contain" onError={fallBackToBrandMark} />
            ) : (
              <span aria-hidden="true" className="rc-sidenav__brand-mark" />
            ),
            title: (
              // d2d Logo Lockup: the wordmark is Britti Sans 500 at line-height 1.0.
              <span className="truncate font-display text-heading-sm font-medium leading-none text-[var(--d2d-text-primary)]">
                {BRAND.name}
              </span>
            ),
            href: '/dashboard',
          }}
          sections={sections}
          renderLink={renderLink}
          collapsed={collapsed}
          onCollapseChange={setCollapsed}
        />
        {railTip.tip}
      </aside>

      <div id="app-main-col" className="flex min-w-0 flex-1 flex-col">
        <AppHeader
          showSearch={false}
          showNotifications={true}
          notificationCount={notifications.count}
          notificationsPanel={notifications.panel}
          notificationsOpen={notifications.open}
          onNotificationsOpenChange={notifications.setOpen}
          showHelp={true}
          helpLabel="User manual"
          onHelpClick={() => navigate('/user-manual')}
          actions={
            <>
              <HeaderStatus />
              <HeaderClock />
            </>
          }
          showThemeSwitcher={true}
          user={{ name: userName, role: user?.email && user.email !== userName ? user.email : undefined }}
          showProfile={true}
          profileLabel="Change password"
          onProfile={() => setPasswordOpen(true)}
          onLogout={() => logout()}
          onToggleSidebar={() => setSidebarOpen(true)}
          tenant={tenantSlug ? { slug: tenantSlug, name: tenantRecord?.name, platform: platformScope } : undefined}
        />

        {/* #app-scroll is the one scrolling region; the screenshot tool measures it.
            relative: the containing block for anything absolutely positioned in a page. */}
        <main className="relative min-h-0 min-w-0 flex-1">
          <div
            id="app-scroll"
            className="h-full overflow-y-auto px-[var(--app-gutter)] pb-[var(--app-gutter)] pt-4 text-[var(--d2d-text-primary)] [--app-gutter:16px] sm:[--app-gutter:24px] lg:[--app-gutter:32px]"
          >
            {children}
          </div>
        </main>
      </div>

      <ChangePasswordDialog
        open={passwordOpen}
        required={mustChangePassword}
        onClose={() => {
          setPasswordOpen(false);
          if (mustChangePassword && user) snoozePasswordPrompt(tenantSlug, user.id);
        }}
        onChanged={() => {
          setPasswordOpen(false);
          if (user) clearPasswordPromptSnooze(tenantSlug, user.id);
          logout();
        }}
      />
    </div>
  );
}
